<template>
  <div>
    <div v-responsive.lg.xl class="country-code-select" ref="selectContainerDesktop">
      <div class="selected-country" @click="toggleDropdown" :class="{ open: isOpen }">
        <span class="flag">{{ selectedCountry.flag }}</span>
        <span class="code">{{ selectedCountry.code }}</span>
        <span class="arrow">▼</span>
      </div>

      <div v-if="isOpen" class="dropdown" ref="dropdownContainer">
        <div class="search-container">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="searchPlaceholder"
            class="search-input"
            ref="searchInput"
          />
        </div>
        <div class="options-container">
          <div
            v-for="country in filteredCountries"
            :key="country.iso2"
            class="option"
            @click="selectCountry(country)"
            :class="{ selected: country.code === selectedCountry.code }"
          >
            <span class="flag">{{ country.flag }}</span>
            <span class="code">{{ country.code }}</span>
            <span class="country-name">{{ country.country }}</span>
            <span v-if="country.code === selectedCountry.code" class="checkmark">✓</span>
          </div>
        </div>
      </div>
    </div>

    <div v-responsive.sm.xs class="country-code-select" ref="selectContainerMobile">
      <div class="selected-country-mobile" @click="toggleDropdown" :class="{ open: isOpen }">
        <span class="flag">{{ selectedCountry.flag }}</span>
        <span class="code">{{ selectedCountry.code }}</span>
        <span class="arrow">▼</span>
      </div>

      <div v-if="isOpen" class="dropdown" ref="dropdownContainer">
        <div class="search-container">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="searchPlaceholder"
            class="search-input"
            ref="searchInputMobile"
          />
        </div>
        <div class="options-container">
          <div
            v-for="country in filteredCountries"
            :key="country.iso2"
            class="option"
            @click="selectCountry(country)"
            :class="{ selected: country.code === selectedCountry.code }"
          >
            <span class="flag">{{ country.flag }}</span>
            <span class="code">{{ country.code }}</span>
            <span class="country-name">{{ country.country }}</span>
            <span v-if="country.code === selectedCountry.code" class="checkmark">✓</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IntlNumber from '../common/intlNumber.json'

export default {
  name: 'CountryCodeSelectProOnline',
  props: {
    value: { type: String, default: '+221' },
    searchPlaceholder: { type: String, default: 'Rechercher un pays...' }
  },
  data() {
    return { isOpen: false, searchQuery: '', countries: IntlNumber }
  },
  computed: {
    selectedCountry() {
      return this.countries.find(c => c.code === this.value) || this.countries.find(c => c.code === '+221')
    },
    filteredCountries() {
      if (!this.searchQuery) return this.countries
      const q = this.searchQuery.toLowerCase()
      return this.countries.filter(c => c.country.toLowerCase().includes(q) || c.code.includes(q))
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
      this.$nextTick(() => {
        const input = this.$refs.searchInput || this.$refs.searchInputMobile
        if (this.isOpen && input) input.focus()
      })
      if (!this.isOpen) this.searchQuery = ''
    },
    selectCountry(country) {
      this.$emit('input', country.code)
      this.isOpen = false
      this.searchQuery = ''
    },
    handleClickOutside(event) {
      const d = this.$refs.selectContainerDesktop
      const m = this.$refs.selectContainerMobile
      const insideD = d && d.contains(event.target)
      const insideM = m && m.contains(event.target)
      if (!insideD && !insideM) {
        this.isOpen = false
        this.searchQuery = ''
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
.country-code-select {
  position: relative;
  display: inline-block;
}

.selected-country,
.selected-country-mobile {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #fafafa;  
  border-bottom: 1px solid rgba(13, 78, 120, 0.22);    
  border-top: 1px solid rgba(13, 78, 120, 0.22); 
  border-left: 1px solid rgba(13, 78, 120, 0.22);
  border-radius: 10px 0 0 10px;       /* arrondi côté gauche */
  cursor: pointer;
  height: 45px;                       /* alignement avec .field-input (45px) */
  box-sizing: border-box;
}

.flag { margin-right: 8px; font-size: 16px; }
.code { flex: 1; font-size: 14px; color: #000; font-weight: 600; margin-right: 8px; }
.arrow { font-size: 10px; color: #000; transition: transform 0.2s ease; }
.selected-country.open .arrow,
.selected-country-mobile.open .arrow { transform: rotate(180deg); }

.dropdown {
  position: absolute;
  top: 110%;
  left: 0;
  z-index: 1000;
  background: #dad8d8;
  border: 1px solid #fafafa;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  width: 260px;
  height: 200px;
  overflow: hidden;
}

.search-container { padding: 8px; border-bottom: 1px solid #dad8d8; background: #dad8d8; }
.search-input {
  width: 100%; padding: 8px 12px; border: 1px solid #333; border-radius: 30px;
  font-size: 14px; outline: none; box-sizing: border-box;
}
.search-input::placeholder { color: black; }

.options-container { max-height: 220px; overflow-y: auto; background: #dad8d8; }
.option {
  display: flex; align-items: center; padding: 8px 10px; cursor: pointer; transition: background-color 0.2s ease;
}
.option:hover { background: #FFB032; }
.option.selected { background: #fff3e0; }

.option .flag { margin-right: 10px; font-size: 16px; }
.option .code { min-width: 50px; font-weight: 500; color: #000; margin-right: 10px; }
.option .country-name { flex: 1; font-size: 12px; color: #000; }
.checkmark { color: #F99B00; font-weight: bold; margin-left: 8px; }

/* Scrollbar */
.options-container::-webkit-scrollbar { width: 6px; }
.options-container::-webkit-scrollbar-track { background: #dad8d8; }
.options-container::-webkit-scrollbar-thumb { background: #000; border-radius: 3px; }
.options-container::-webkit-scrollbar-thumb:hover { background: #000; }
</style>